/**
 * Vision API 에러 핸들링 유틸리티
 *
 * 에러 분류, 메시지 생성, 재시도 전략 결정
 *
 * 에러 유형별 처리:
 * - NETWORK_ERROR: 재시도 가능, 게임 모드 전환 가능
 * - TIMEOUT: 재시도 가능, 게임 모드 전환 가능
 * - INVALID_IMAGE: 재시도 불가, 다시 촬영 필요
 * - SERVER_ERROR: 재시도 가능 (일시적), 게임 모드 전환 가능
 * - PERMISSION_DENIED: 재시도 불가, 강사 권한 필요
 */

import { AxiosError } from 'axios';
import type { VisionError, VisionErrorCode, RetryConfig } from '../types/vision';

/**
 * 기본 재시도 설정
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000, // 1초
  backoffMultiplier: 2, // 지수 백오프
};

/**
 * 에러 코드별 메시지 정의
 */
const ERROR_MESSAGES: Record<VisionErrorCode, string> = {
  NETWORK_ERROR: '네트워크 연결을 확인해주세요',
  TIMEOUT: '서버 응답 시간이 초과되었습니다. 다시 시도해주세요',
  INVALID_IMAGE: '이미지를 인식할 수 없습니다. 더 선명하게 다시 촬영해주세요',
  NO_ANSWERS_FOUND: '답안을 찾을 수 없습니다. 답안이 잘 보이도록 다시 촬영해주세요',
  PERMISSION_DENIED: '재채점 권한이 없습니다. 선생님께 문의해주세요',
  SERVER_ERROR: '서버에 일시적인 오류가 발생했습니다',
  PAGE_NOT_FOUND: '이 페이지에 등록된 문제가 없습니다. 선생님께 문의해주세요',
  UNKNOWN_ERROR: '알 수 없는 오류가 발생했습니다',
};

/**
 * 에러 코드별 재시도 가능 여부
 */
const RETRYABLE_ERRORS = new Set<VisionErrorCode>([
  'NETWORK_ERROR',
  'TIMEOUT',
  'SERVER_ERROR',
]);

/**
 * 에러 코드별 게임 모드 전환 가능 여부
 */
const FALLBACK_AVAILABLE_ERRORS = new Set<VisionErrorCode>([
  'NETWORK_ERROR',
  'TIMEOUT',
  'SERVER_ERROR',
  'NO_ANSWERS_FOUND',
  'PAGE_NOT_FOUND',
  'UNKNOWN_ERROR',
]);

/**
 * 에러를 VisionError로 분류합니다.
 *
 * @param error 발생한 에러
 * @returns 분류된 VisionError
 *
 * @example
 * ```typescript
 * try {
 *   await gradePage(image, workbook, page);
 * } catch (err) {
 *   const visionError = classifyError(err);
 *   if (visionError.retryable) {
 *     // 재시도 로직
 *   }
 * }
 * ```
 */
export function classifyError(error: unknown): VisionError {
  const code = determineErrorCode(error);

  return {
    code,
    message: ERROR_MESSAGES[code],
    retryable: RETRYABLE_ERRORS.has(code),
    fallbackAvailable: FALLBACK_AVAILABLE_ERRORS.has(code),
    originalError: error,
  };
}

/**
 * 에러 코드에 해당하는 사용자 메시지를 반환합니다.
 *
 * @param code 에러 코드
 * @returns 사용자에게 표시할 메시지
 */
export function getErrorMessage(code: VisionErrorCode): string {
  return ERROR_MESSAGES[code];
}

/**
 * 재시도 여부를 결정합니다.
 *
 * @param error VisionError 객체
 * @param attemptCount 현재 시도 횟수 (1부터 시작)
 * @param config 재시도 설정
 * @returns 재시도 가능 여부
 */
export function shouldRetry(
  error: VisionError,
  attemptCount: number,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): boolean {
  // 재시도 불가능한 에러 유형
  if (!error.retryable) {
    return false;
  }

  // 최대 재시도 횟수 초과
  if (attemptCount >= config.maxAttempts) {
    return false;
  }

  return true;
}

/**
 * 다음 재시도까지 대기 시간을 계산합니다 (지수 백오프).
 *
 * @param attemptCount 현재 시도 횟수 (1부터 시작)
 * @param config 재시도 설정
 * @returns 대기 시간 (ms)
 *
 * @example
 * // attemptCount=1 → 1000ms
 * // attemptCount=2 → 2000ms
 * // attemptCount=3 → 4000ms
 */
export function calculateRetryDelay(
  attemptCount: number,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): number {
  return config.baseDelay * Math.pow(config.backoffMultiplier, attemptCount - 1);
}

/**
 * 에러를 로깅합니다 (디버깅용).
 *
 * @param error VisionError 객체
 * @param context 추가 컨텍스트 정보
 */
export function logVisionError(
  error: VisionError,
  context?: Record<string, unknown>
): void {
  console.error('[Vision Error]', {
    code: error.code,
    message: error.message,
    retryable: error.retryable,
    fallbackAvailable: error.fallbackAvailable,
    ...context,
  });

  if (error.originalError) {
    console.error('[Original Error]', error.originalError);
  }
}

/**
 * 대기 후 Promise를 반환하는 유틸리티
 *
 * @param ms 대기 시간 (ms)
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ============================================================================
// Private Helpers
// ============================================================================

/**
 * 에러 유형에서 에러 코드를 추론합니다.
 */
function determineErrorCode(error: unknown): VisionErrorCode {
  // Axios 에러 처리
  if (isAxiosError(error)) {
    return classifyAxiosError(error);
  }

  // 타임아웃 에러
  if (isTimeoutError(error)) {
    return 'TIMEOUT';
  }

  // 네트워크 에러
  if (isNetworkError(error)) {
    return 'NETWORK_ERROR';
  }

  // 문자열 에러 메시지 분석
  if (error instanceof Error) {
    return classifyErrorMessage(error.message);
  }

  return 'UNKNOWN_ERROR';
}

/**
 * Axios 에러 타입 체크
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (
    error !== null &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Axios 에러를 에러 코드로 분류
 */
function classifyAxiosError(error: AxiosError): VisionErrorCode {
  // 네트워크 에러 (요청이 서버에 도달하지 못함)
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'TIMEOUT';
    }
    return 'NETWORK_ERROR';
  }

  const status = error.response.status;
  const data = error.response.data as Record<string, unknown> | undefined;

  // HTTP 상태 코드 기반 분류
  switch (status) {
    case 400:
      // Bad Request - 이미지 문제일 가능성
      if (data?.detail && typeof data.detail === 'string') {
        if (
          data.detail.includes('image') ||
          data.detail.includes('이미지')
        ) {
          return 'INVALID_IMAGE';
        }
        if (
          data.detail.includes('answers') ||
          data.detail.includes('답안')
        ) {
          return 'NO_ANSWERS_FOUND';
        }
      }
      return 'INVALID_IMAGE';

    case 401:
    case 403:
      return 'PERMISSION_DENIED';

    case 404:
      // 페이지에 등록된 문제가 없는 경우
      return 'PAGE_NOT_FOUND';

    case 408:
    case 504:
      return 'TIMEOUT';

    case 500:
    case 502:
    case 503:
      return 'SERVER_ERROR';

    default:
      if (status >= 500) {
        return 'SERVER_ERROR';
      }
      return 'UNKNOWN_ERROR';
  }
}

/**
 * 타임아웃 에러 체크
 */
function isTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.name === 'TimeoutError' ||
      error.message.includes('timeout') ||
      error.message.includes('Timeout')
    );
  }
  return false;
}

/**
 * 네트워크 에러 체크
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.name === 'NetworkError' ||
      error.message.includes('network') ||
      error.message.includes('Network') ||
      error.message.includes('Failed to fetch')
    );
  }
  return false;
}

/**
 * 에러 메시지를 분석하여 에러 코드 추론
 */
function classifyErrorMessage(message: string): VisionErrorCode {
  const lowerMessage = message.toLowerCase();

  if (
    lowerMessage.includes('timeout') ||
    lowerMessage.includes('시간 초과')
  ) {
    return 'TIMEOUT';
  }

  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('네트워크') ||
    lowerMessage.includes('connection')
  ) {
    return 'NETWORK_ERROR';
  }

  if (
    lowerMessage.includes('image') ||
    lowerMessage.includes('이미지') ||
    lowerMessage.includes('invalid')
  ) {
    return 'INVALID_IMAGE';
  }

  if (
    lowerMessage.includes('permission') ||
    lowerMessage.includes('권한') ||
    lowerMessage.includes('denied')
  ) {
    return 'PERMISSION_DENIED';
  }

  return 'UNKNOWN_ERROR';
}
